import React, { useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Collapse,
  Button,
  Alert,
  Chip,
} from '@mui/material';
import {
  FlightTakeoff,
  FlightLand,
  ConnectingAirports,
  Luggage,
  ExpandMore,
  ExpandLess,
  WarningAmber,
} from '@mui/icons-material';
import { formatItineraryTime, formatLayoverDuration, hasMultiLegJourney } from '../../utils/flightItinerary';

const segmentLabel = (seg) =>
  `${seg.airlineName || seg.airlineCode || 'Flight'} ${seg.airlineCode || ''}${seg.flightNumber || ''}`.trim();

const FlightItineraryPanel = ({ offer, defaultExpanded = false, compact = false }) => {
  const [open, setOpen] = useState(defaultExpanded);
  const multiLeg = hasMultiLegJourney(offer);
  const slices = offer?.slices?.length ? offer.slices : [];

  if (!multiLeg && !offer?.baggage?.summary) return null;

  const panelBody = (
    <Stack spacing={2} sx={{ mt: compact ? 0 : 1 }}>
      {offer.baggage?.summary && (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Luggage sx={{ fontSize: 18, color: 'text.secondary', mt: 0.25 }} />
          <Box>
            <Typography variant="body2" fontWeight={600}>
              Baggage (indicative)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {offer.baggage.summary}
            </Typography>
          </Box>
        </Stack>
      )}

      {slices.map((slice, si) => (
        <Box key={`slice-${si}`}>
          {slices.length > 1 && (
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ mb: 1, display: 'block' }}>
              {slice.label || (si === 0 ? 'Outbound' : 'Return')}
            </Typography>
          )}
          {(slice.segments || []).map((seg, i) => (
            <Box key={`seg-${si}-${i}`}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
                <FlightTakeoff sx={{ fontSize: 16, color: 'secondary.main' }} />
                <Typography variant="body2">
                  <strong>{seg.origin}</strong> {formatItineraryTime(seg.departsAt)} — {segmentLabel(seg)} →{' '}
                  <strong>{seg.destination}</strong> {formatItineraryTime(seg.arrivesAt)}
                </Typography>
              </Stack>

              {slice.layovers?.[i] && (
                <Box
                  sx={{
                    ml: 2,
                    pl: 2,
                    borderLeft: '2px dashed',
                    borderColor: slice.layovers[i].isTightConnection ? 'warning.main' : 'divider',
                    py: 1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <ConnectingAirports sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Stop at <strong>{slice.layovers[i].airport}</strong> — {formatLayoverDuration(slice.layovers[i].durationMinutes)}
                    </Typography>
                    {slice.layovers[i].isTightConnection && (
                      <Chip icon={<WarningAmber />} label="Tight connection" size="small" color="warning" variant="outlined" />
                    )}
                    {slice.layovers[i].changeAirline && (
                      <Chip label="Change airline" size="small" variant="outlined" />
                    )}
                  </Stack>
                  {slice.layovers[i].baggageNote && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                      {slice.layovers[i].baggageNote}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      ))}

      {(offer.connectionNotes || []).length > 0 && (
        <Alert severity="info" icon={<FlightLand />} sx={{ py: 0.5 }}>
          {(offer.connectionNotes || []).slice(0, 3).map((note, i) => (
            <Typography key={i} variant="caption" display="block">
              {note}
            </Typography>
          ))}
        </Alert>
      )}
    </Stack>
  );

  if (compact) return panelBody;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Button
        size="small"
        color="secondary"
        onClick={() => setOpen((v) => !v)}
        startIcon={open ? <ExpandLess /> : <ExpandMore />}
        sx={{ textTransform: 'none', px: 0 }}
      >
        {open ? 'Hide journey details' : 'View full journey & luggage'}
      </Button>
      <Collapse in={open}>{panelBody}</Collapse>
    </Box>
  );
};

export default FlightItineraryPanel;
